import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

const functions = getFunctions(getApp());

export interface ResolvedUser {
  name: string;
  email: string;
}

// Admin-only: resolve display names/emails for a set of UIDs from Firebase Auth.
// Used by the admin chat to show real names instead of raw UIDs for accounts
// (e.g. gift-code redeemers) that never had a name written to Firestore.
export async function resolveUserNames(uids: string[]): Promise<Record<string, ResolvedUser>> {
  if (uids.length === 0) return {};
  const fn = httpsCallable<{ uids: string[] }, { users: Record<string, ResolvedUser> }>(functions, 'resolveUserNames');
  const result = await fn({ uids });
  return result.data.users;
}

// ─── Shared, persisted name cache ───
// Warmed at app load (from AppShell's admin chats listener) so the chat page can
// render real names immediately instead of flickering UID → name on first open.
const LS_KEY = 'gnosis-resolved-users';
const resolvedCache: Record<string, ResolvedUser> = loadCache();
// Per-session dedupe of lookups. Deliberately NOT seeded from the persisted
// cache, so each app load re-warms once in the background to keep names fresh
// while still showing the cached value instantly.
const requestedUids = new Set<string>();

function loadCache(): Record<string, ResolvedUser> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}
function persistCache(): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(resolvedCache)); } catch { /* ignore quota/SSR */ }
}

// Snapshot of currently-known names — for initializing component state so the
// first paint already has them.
export function getResolvedUsers(): Record<string, ResolvedUser> {
  return { ...resolvedCache };
}

// Resolve any not-yet-fetched UIDs, merge into the shared cache, persist, and
// return the full cache. Safe to call repeatedly and from multiple places — it
// dedupes in-flight lookups and no-ops once a UID is known.
export async function warmUserNames(uids: string[]): Promise<Record<string, ResolvedUser>> {
  const missing = uids.filter((id) => id && !requestedUids.has(id));
  if (missing.length) {
    missing.forEach((id) => requestedUids.add(id));
    try {
      const r = await resolveUserNames(missing);
      Object.assign(resolvedCache, r);
      persistCache();
    } catch {
      missing.forEach((id) => requestedUids.delete(id)); // allow retry next call
    }
  }
  return { ...resolvedCache };
}
